/**
 * Allow-list HTML sanitiser for step instructions (ProcedureStepView.bodyHtml). Written by
 * procedure editors (trusted staff) and sometimes by the AI import, so only plain formatting
 * survives: paragraphs, lists, emphasis, headings, tables and safe links. Everything else is
 * unwrapped to its text; scripts, styles, frames and event handlers are dropped.
 */
const ALLOWED = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'mark', 'small', 'sub', 'sup',
  'ul', 'ol', 'li', 'h2', 'h3', 'h4', 'h5', 'blockquote', 'code', 'pre', 'hr',
  'a', 'span', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption',
]);
const DROP = new Set(['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'form', 'input', 'button', 'textarea', 'select', 'svg', 'math', 'template', 'noscript']);

function safeHref(href: string | null): string | null {
  if (!href) return null;
  const v = href.trim();
  return /^(https?:|mailto:|tel:)/i.test(v) ? v : null;
}

function clean(node: Node, doc: Document): Node[] {
  if (node.nodeType === Node.TEXT_NODE) return [doc.createTextNode(node.textContent ?? '')];
  if (node.nodeType !== Node.ELEMENT_NODE) return [];
  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (DROP.has(tag)) return [];
  const children = Array.from(el.childNodes).flatMap((c) => clean(c, doc));
  if (!ALLOWED.has(tag)) return children; // unwrap unknown tags (keep their text)
  const out = doc.createElement(tag);
  if (tag === 'a') {
    const href = safeHref(el.getAttribute('href'));
    if (href) {
      out.setAttribute('href', href);
      out.setAttribute('target', '_blank');
      out.setAttribute('rel', 'noopener noreferrer');
    }
  }
  if (tag === 'td' || tag === 'th') {
    const colspan = el.getAttribute('colspan');
    if (colspan && /^\d{1,2}$/.test(colspan)) out.setAttribute('colspan', colspan);
  }
  children.forEach((c) => out.appendChild(c));
  return [out];
}

export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html.replace(/<[^>]*>/g, '');
  }
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const target = document.implementation.createHTMLDocument('');
  const wrap = target.createElement('div');
  Array.from(doc.body.childNodes)
    .flatMap((n) => clean(n, target))
    .forEach((n) => wrap.appendChild(n));
  return wrap.innerHTML;
}

/** True when the HTML has visible text (an editor's empty "<p></p>" counts as empty). */
export function hasText(html: string | null | undefined): boolean {
  return !!html && html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
}
